import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  MEETING_STATUS,
  MEETING_SUBTYPES,
  MEETING_TYPES,
} from "@/lib/meetings-constants";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { Calendar, FileText, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  date: z.string().min(1, "Date is required"),
  meetingType: z.enum([
    "city-council",
    "planning-zoning",
    "board-adjustment",
    "historic-preservation-committee",
  ]),
  meetingSubtype: z
    .enum(["regular", "special-meeting", "public-hearing", "public-notice"])
    .optional()
    .nullable(),
  location: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  status: z.enum(["scheduled", "completed", "cancelled"]),
});

type FormData = z.infer<typeof formSchema>;

function EditMeetingRoute() {
  const { meetingId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const [selectedAgenda, setSelectedAgenda] = useState<string | undefined>();
  const [selectedMinutes, setSelectedMinutes] = useState<string | undefined>();

  // Get the meeting data
  const meetingQuery = useSuspenseQuery(
    trpc.meetings.getById.queryOptions({ id: meetingId }),
  );
  const meeting = meetingQuery.data;

  // Get available media for agenda/minutes selection
  const mediaQuery = useQuery(trpc.media.list.queryOptions());
  const media = mediaQuery.data || [];

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Set initial form values when meeting data loads
  useEffect(() => {
    if (meeting) {
      reset({
        title: meeting.title,
        date: format(new Date(meeting.date), "yyyy-MM-dd"),
        // biome-ignore lint/suspicious/noExplicitAny: Form expects string type but DB returns enum
        meetingType: meeting.meetingType as any,
        // biome-ignore lint/suspicious/noExplicitAny: Form expects string type but DB returns enum
        meetingSubtype: meeting.meetingSubtype as any,
        location: meeting.location || undefined,
        description: meeting.description || undefined,
        // biome-ignore lint/suspicious/noExplicitAny: Form expects string type but DB returns enum
        status: meeting.status as any,
      });
      setSelectedAgenda(meeting.agendaMedia?.id);
      setSelectedMinutes(meeting.minutesMedia?.id);
    }
  }, [meeting, reset]);

  // Clear stale media selections when media list updates
  useEffect(() => {
    const mediaIds = new Set(media.map((m) => m.id));

    if (selectedAgenda && !mediaIds.has(selectedAgenda)) {
      setSelectedAgenda(undefined);
      toast.warning(
        "The selected agenda document is no longer available and has been cleared.",
      );
    }

    if (selectedMinutes && !mediaIds.has(selectedMinutes)) {
      setSelectedMinutes(undefined);
      toast.warning(
        "The selected minutes document is no longer available and has been cleared.",
      );
    }
  }, [media, selectedAgenda, selectedMinutes]);

  const updateMutation = useMutation(
    trpc.meetings.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["meetings"] });
        queryClient.invalidateQueries({ queryKey: ["media"] });
        navigate({ to: "/meetings" });
      },
      onError: (error) => {
        // Show user-friendly error message
        toast.error(error.message || "Failed to update meeting");
      },
    }),
  );

  const onSubmit = async (data: FormData) => {
    await updateMutation.mutateAsync({
      id: meetingId,
      ...data,
      date: new Date(data.date),
      agendaMediaId: selectedAgenda || null,
      minutesMediaId: selectedMinutes || null,
    });
  };

  return (
    <div className="mx-auto grid max-w-3xl gap-4">
      <div className="flex items-center gap-4">
        <h1 className="text-3xl font-semibold">Edit Meeting</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Meeting Details</CardTitle>
            <CardDescription>Update the meeting information.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Regular City Council Meeting"
                {...register("title")}
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="date"
                    type="date"
                    className="pl-10"
                    {...register("date")}
                  />
                </div>
                {errors.date && (
                  <p className="text-sm text-red-500">{errors.date.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="meetingType">Meeting Type</Label>
                <Controller
                  name="meetingType"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="meetingSubtype">Meeting Subtype</Label>
                <Controller
                  name="meetingSubtype"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => field.onChange(value || null)}
                      value={field.value || ""}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subtype (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {MEETING_SUBTYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_STATUS.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="e.g., City Hall, Room 201"
                {...register("location")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description or notes about the meeting"
                rows={3}
                {...register("description")}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>
              Update agenda and minutes documents for this meeting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Agenda Document</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedAgenda || ""}
                  onValueChange={(value) =>
                    setSelectedAgenda(value || undefined)
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select existing document" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {media.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: "/media/new" })}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New
                </Button>
              </div>
              {selectedAgenda && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Agenda selected
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Minutes Document</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedMinutes || ""}
                  onValueChange={(value) =>
                    setSelectedMinutes(value || undefined)
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select existing document" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {media.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: "/media/new" })}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload New
                </Button>
              </div>
              {selectedMinutes && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Minutes selected
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={isSubmitting || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Updating..." : "Update Meeting"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: "/meetings" })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/meetings/$meetingId")({
  component: () => <EditMeetingRoute />,
});
